import { memo, useReducer } from 'react';
import { Link } from 'react-router';

import { HELP_EMAIL } from 'my-core/constants';
import { getCourseConfig } from 'my-core/course-utils';
import useOnlineCourseBookletPrint from 'my-core/hooks/useOnlineCourseBookletPrint';
import { studentCoursePageFlashCardsRelativePath } from 'my-core/routes';
import { PROMO_POINTS, SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT } from 'my-core/subscription-utils';

import CourseBookletImage from 'my-components/CoursePage/CourseBookletImage';
import FlashCardDeckImage from 'my-components/FlashCardDeck/FlashCardDeckImage';
import CustomColorButton from 'my-elements/CustomColorButton';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import { faFileArrowDown, faRocket } from '@fortawesome/pro-regular-svg-icons';
import { faCheck, faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export function StudentCoursePageContentsWrapper({ children }) {
  return (
    <Box
      sx={{
        p: { xs: 1, md: 2, lg: 3 },
        pb: 5,
        flex: 1,
        overflow: 'auto',
        width: '100%',
        maxWidth: ({ constants }) => constants.WRAPPER_MAX_WIDTH_LG,
        mx: 'auto',
        containerType: 'inline-size',
        containerName: 'contents',
      }}
    >
      {children}
    </Box>
  );
}

export function StudentCoursePageTwoColumnLayout({ children }) {
  return (
    <div
      css={({ breakpoints, spacing }) => {
        const oneColumnLayout = {
          flexDirection: 'column',
          gap: spacing(2),
          '& > div': { flex: 1, gap: spacing(2), '&:last-of-type': { width: '100%' } },
          '& .oneColumnVisible': { display: 'block' },
          '& .oneColumnHidden': { display: 'none' },
        };
        return {
          display: 'flex',
          gap: spacing(3),
          '& > div': {
            containerType: 'inline-size',
            display: 'flex',
            flexDirection: 'column',
            gap: spacing(3),
            '&:first-of-type': { flex: 1 },
            '&:last-of-type': { flex: '0 0 auto', width: 350, '&:empty': { display: 'none' } },
          },
          '& .oneColumnVisible': { display: 'none' },
          '& .oneColumnHidden': { display: 'block' },
          [breakpoints.down('md')]: oneColumnLayout,
          [`@container contents (max-width: ${breakpoints.values.md}px)`]: oneColumnLayout,
        };
      }}
    >
      {children}
    </div>
  );
}

export function StudentCoursePageSectionHeader({ title, ...rest }) {
  return (
    <Typography
      variant="h6"
      {...rest}
      sx={[
        {
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          mb: 2,
          typography: { md: 'h5' },
        },
        ...(Array.isArray(rest.sx) ? rest.sx : [rest.sx]),
      ]}
    >
      {title}
    </Typography>
  );
}

export function StudentCoursePageCard({ children, className, sx }) {
  return (
    <Paper className={className} elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', ...sx }}>
      {children}
    </Paper>
  );
}

// Shared shell for this page's product cards: a title row above a square product image, an info
// column and an optional price/actions rail. Once the card is narrower than `sm` — by viewport or
// by its own container — the rail stacks underneath and the image goes full-bleed across the top,
// swapping in a wide banner where the product has one.
export function GradeBoosterCardWrapper({ actions, borderColor, children, image, imageBanner, imageColor, title }) {
  return (
    <div
      css={({ breakpoints, palette, shape, spacing }) => {
        const mobileCss = {
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),

          '& > .cardContents': { display: 'contents' },
          '& > .cardTitle': { marginBottom: 0 },
          '& .cardImage': {
            order: -1,
            width: `calc(100% + ${spacing(6)})`,
            margin: spacing(-2, -3, 2),
            borderRadius: 0,
            height: 'auto',
            aspectRatio: 5,
            objectFit: 'cover',
            ...(imageBanner && { content: `url(${imageBanner})` }),
          },
          '& .cardDivider': { width: '100%', height: 1 },
          '& .cardActions': { flex: 0, width: '100%' },
        };
        return {
          backgroundColor: palette.background.paper,
          borderRadius: shape.borderRadius,
          padding: spacing(2, 3),
          overflow: 'hidden',
          ...(borderColor && { boxShadow: `0 0 4px ${palette[borderColor].main}` }),
          '& > .cardTitle': { marginBottom: spacing(2) },
          '& > .cardContents': { display: 'flex', gap: spacing(2) },
          '& .cardImage': {
            width: 75,
            height: 75,
            content: `url(${image})`,
            objectFit: 'contain',
            backgroundColor: palette[imageColor]?.light,
            borderRadius: shape.borderRadiusSm,
          },
          '& .cardInfo': { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, gap: spacing(1) },
          '& .cardDivider': { alignSelf: 'center', width: 1, height: 100, backgroundColor: palette.divider },
          '& .cardActions': { flex: '0 0 180px', alignSelf: 'center' },
          [`@container (max-width: ${breakpoints.values.sm}px)`]: mobileCss,
          [breakpoints.down('sm')]: mobileCss,
        };
      }}
    >
      <div className="cardTitle">{title}</div>
      <div className="cardContents">
        <img className="cardImage" />
        <div className="cardInfo">{children}</div>
        {!!actions && (
          <>
            <div className="cardDivider" />
            <div className="cardActions">{actions}</div>
          </>
        )}
      </div>
    </div>
  );
}

export const StudentCoursePageBookletCard = memo(function StudentCoursePageBookletCard({
  className,
  classPassId,
  course,
}) {
  const [autoPrint, printing] = useOnlineCourseBookletPrint();
  return (
    <section className={className}>
      <StudentCoursePageSectionHeader title="Course Notes" />
      <StudentCoursePageCard
        css={({ breakpoints, spacing }) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: spacing(2),
          '& > .booklet-image': { width: '80%', maxWidth: 200 },
          '& > .details': { display: 'flex', flexDirection: 'column', gap: spacing(2) },
          [`@container (min-width: ${breakpoints.values.sm}px)`]: {
            flexDirection: 'row',
            '& .cta': { marginLeft: 'auto' },
          },
        })}
      >
        <CourseBookletImage
          className="booklet-image"
          subjectConfig={getCourseConfig(course)}
          subtitle="Course Notes"
          title={course.code}
        />
        <div className="details">
          <Typography variant="body2">
            Wizeprep&apos;s course notes with hundreds of easy-to-understand explanations, diagrams, and practice
            problems.
          </Typography>
          <Button
            className="cta"
            disabled={printing}
            onClick={() => autoPrint(classPassId)}
            size="small"
            startIcon={<FontAwesomeIcon icon={faFileArrowDown} />}
            variant="outlined"
          >
            Download Course Notes
          </Button>
        </div>
      </StudentCoursePageCard>
    </section>
  );
});

export function StudentCoursePageFlashCardsDeckCard({ course, flashCardDecks }) {
  const title = course.code;
  return (
    <section>
      <StudentCoursePageSectionHeader title="Flashcards" />
      <StudentCoursePageCard
        css={({ breakpoints, spacing }) => ({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: spacing(2),
          '& > .image': { width: '100%' },
          '& > .details': { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: spacing(2) },
          [`@container (min-width: ${breakpoints.values.sm}px)`]: {
            flexDirection: 'row',
            '& > .image': { maxWidth: 300 },
          },
        })}
      >
        <FlashCardDeckImage className="image" title={title} />
        <div className="details">
          <Typography variant="body2">Study important concepts from your course.</Typography>
          <Button
            component={Link}
            nativeButton={false}
            to={studentCoursePageFlashCardsRelativePath(flashCardDecks[0])}
            variant="outlined"
          >
            Study With Flashcards
          </Button>
        </div>
      </StudentCoursePageCard>
    </section>
  );
}

export function GradeBoosterSubscriptionOffer({ onSubscribe, studySessions }) {
  const isAllSubscriptionSessions = studySessions.every(ss => ss.purchase_type === 'subscription');

  return (
    <div
      css={({ breakpoints, palette, shape, spacing }) => ({
        borderRadius: shape.borderRadiusSm,
        color: palette.common.white,
        padding: spacing(3),
        background: 'linear-gradient(180deg, #4E18B7 0%, #546BF8 100%)',
        display: 'grid',
        gridTemplateAreas: "'title' 'checks' 'cta'",
        gap: spacing(2),
        '& > .title': { gridArea: 'title' },
        '& > .checks': {
          gridArea: 'checks',
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),
          '& > div': {
            display: 'flex',
            alignItems: 'center',
            gap: spacing(1),
            '& > svg': { color: palette.text.secondary },
          },
        },
        '& > .cta': { gridArea: 'cta' },
        [`@container (min-width: ${breakpoints.values.sm}px)`]: {
          gridTemplateAreas: "'title checks' 'cta checks'",
          rowGap: spacing(1),
          '& > .title': { alignSelf: 'end' },
          '& > .cta': { alignSelf: 'start' },
        },
      })}
    >
      <Typography align="center" className="title" variant="h6">
        {isAllSubscriptionSessions ?
          'Better Grades, Less Studying with Wizeprep Plus'
        : `Save ${SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT}% With Wizeprep Plus`}
      </Typography>
      <div className="checks">
        {[
          ...PROMO_POINTS,
          isAllSubscriptionSessions ? 'Grade Boosters included' : (
            `${SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT}% off all Grade Boosters`
          ),
        ].map(p => (
          <div key={p}>
            <FontAwesomeIcon icon={faCheck} />
            <Typography>{p}</Typography>
          </div>
        ))}
      </div>
      <CustomColorButton className="cta" color="white" fullWidth onClick={onSubscribe} variant="outlined">
        Subscribe to Save
      </CustomColorButton>
    </div>
  );
}

export function GradeBoostersExplanation() {
  return (
    <StudentCoursePageCard>
      <Typography gutterBottom variant="subtitle1">
        <FontAwesomeIcon icon={faRocket} /> What Are Grade Boosters?
      </Typography>
      <Typography>
        Grade boosters are extra sessions that are designed by expert instructors specifically for your course. They use
        course material and previous exams to design content that is as close as possible to your actual tests and
        lectures.
      </Typography>
    </StudentCoursePageCard>
  );
}

export function StudentCoursePageFAQsCard({ boosterType }) {
  return (
    <section>
      <StudentCoursePageSectionHeader title="FAQs" />
      <StudentCoursePageCard>
        <StudentCoursePageFAQsList boosterType={boosterType} />
      </StudentCoursePageCard>
    </section>
  );
}

export function StudentCoursePageFAQsList({ boosterType }) {
  const [openQuestions, toggleOpenQuestion] = useReducer(
    (questions, question) =>
      questions.includes(question) ? questions.filter(q => q !== question) : [question, ...questions],
    [],
  );
  return (
    <div
      css={({ palette, shape: { borderRadiusSm }, spacing }) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: spacing(2),
        '& > .faq': {
          backgroundColor: palette.background.default,
          padding: spacing(1.5, 2),
          borderRadius: borderRadiusSm,
          '& > .faqHeader': {
            display: 'flex',
            alignItems: 'center',
            gap: spacing(1),
            '& > .faqQuestion': { flex: 1 },
          },
          '& .faqAnswer': {
            marginTop: spacing(1),
            whiteSpace: 'pre-line',
          },
        },
      })}
    >
      {FAQS.filter(f => !f.type || f.type === boosterType).map(({ answer, question }) => {
        const open = openQuestions.includes(question);
        return (
          <div key={question} className="faq">
            <div className="faqHeader">
              <Typography className="faqQuestion">{question}</Typography>
              <IconButton edge="end" onClick={() => toggleOpenQuestion(question)}>
                <FontAwesomeIcon icon={open ? faMinus : faPlus} size="xs" />
              </IconButton>
            </div>
            <Collapse in={open} mountOnEnter>
              <FAQAnswer answer={answer} />
            </Collapse>
          </div>
        );
      })}
    </div>
  );
}
function FAQAnswer({ answer }) {
  return typeof answer === 'string' ?
      <Typography className="faqAnswer" variant="body2">
        {answer}
      </Typography>
    : answer();
}

const FAQS = [
  {
    question: 'How long will I have access to these resources?',
    answer:
      'You will be able to access Mock Exam and Crash Course content up until the date of the exam or test it covers. The Weekly Tutorial Bundle is available until the end of the semester.',
  },
  {
    question: 'What is the refund policy?',
    answer: () => (
      <>
        <Typography className="faqAnswer" variant="body2">
          Grade Boosters that are scheduled to take place live are refundable up to 48 hours before the start time of
          the respective first live session. To request a refund, you can email{' '}
          <MuiLink href={`mailto:${HELP_EMAIL}`}>{HELP_EMAIL}</MuiLink>. No refunds are possible if the refund is
          requested anytime after 48 hours before the date/time of the first live session of the respective Grade
          Booster.
          <br />
          <br />
          Grade Boosters that are recordings/on-demand in nature are non-refundable after they&apos;ve been purchased.
          <br />
          <br />
          Please note, that Wizeprep reserves the right to modify:
        </Typography>
        <Typography className="faqAnswer" component="ol" variant="body2">
          <li>The instructor at our discretion for any live Grade Booster at any time</li>
          <li>
            The date/time of any live grade booster as long as it is rescheduled to take place within 24 hours of the
            originally advertised date/time and as long as the recording of the live event will be made available for
            students to be able to watch at their convenience. If Wizeprep changes the date/time for any live Grade
            Booster by more than 24 hours of the originally advertised date/time, the student can elect to be refunded
            for that live session or in the case that it is a part of a live session, they will be refunded on a
            pro-rated basis for the respective part of the live session that has been changed.
          </li>
        </Typography>
        <br />
        <Typography variant="body2">
          Do you still have questions? Find more answers in our{' '}
          <MuiLink href="https://help.wizeprep.com/en/" rel="noreferrer" target="_blank">
            Help Center
          </MuiLink>
          .
        </Typography>
      </>
    ),
  },
  {
    question: 'How many devices can I watch the recordings from?',
    answer: 'You can authorize 2 devices to view recordings from.',
  },
  {
    question: 'Is this included in the Wizeprep Plus subscription?',
    answer: `No, this product is sold separately, but Wizeprep Plus members get ${SUBSCRIPTION_PRODUCT_DISCOUNT_PERCENT}% off.`,
  },
  {
    question: "How does the instructor know what's on my exams?",
    answer:
      'Wizeprep instructors analyze tons of past exams so they know the types of questions and topics that will be covered in your exam. They design new questions to test your knowledge on these topics and present them in the same way you will see on your exams so you can be prepared and confident on test day.',
  },
  {
    question: 'How do I watch the recording?',
    answer:
      'You can watch the recording from your grade-booster page. You will be notified once it becomes available. You can watch the recording on up to 2 unique devices.',
  },
  {
    question: "What if I can't attend a live session?",
    answer:
      'All live sessions are recorded. The recorded sessions are available 24 hours after the event and up until the date of the exam or test it covers.',
  },
  {
    question: 'Should I fill out the questions before watching the video?',
    answer:
      'To get the most out of your session, we strongly recommend that you review your session booklet and complete any practice problems included before the session’s start time.',
  },
  {
    question: 'When will I get access to additional resources like the booklet and solutions?',
    answer:
      'For pre-recorded sessions, all resources are released on the release date.\n\nFor live events, the booklet is available 24 hours before the start time. If your session includes solutions, those will be available 24 hours after the session ends.',
  },
  {
    question: 'How do I access the mock exam?',
    answer:
      'You can do the mock exam on your own time using the Wizeprep Mock Exam feature, which is available from your student dashboard, the course page, and from the email confirmation you will receive.',
    type: 'on_demand_mock',
  },
  {
    question: 'How do I join a live session?',
    answer:
      'If your session is marked as “live,” you can join it from the course overview page, the grade boosters page, and the link in your email. Simply click the “join” button.',
  },
  {
    question: 'When can I access the recording?',
    answer:
      'Pre-recorded sessions will be released on the release date specified. For live sessions, recordings are released within 24 hours of the end of the live session.\n\nYou can watch the recording on up to 2 unique devices.',
  },
];
